import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3-geo';
import { feature } from 'topojson-client';
import styled from 'styled-components';
import { areas } from '../config';

const MAP_URL =
    'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const MapWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background:
        radial-gradient(circle at center, rgba(23, 100, 152, 0.12), transparent 65%),
        linear-gradient(180deg, #050b12 0%, #020508 100%);
    cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
`;

const MapSvg = styled.svg`
    width: 100%;
    height: 100%;
    display: block;

    .country {
        transition:
        fill 180ms ease,
        stroke 180ms ease,
        opacity 180ms ease,
        filter 180ms ease;
    }

    .country:hover {
        filter: brightness(1.2);
    }
`;

const Loading = styled.div`
    color: #888;
    font-size: 14px;
`;

const Tooltip = styled.div`
    position: absolute;
    pointer-events: none;
    transform: translate(-50%, -120%);
    padding: 7px 10px;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    font-size: 12px;
    white-space: nowrap;
    z-index: 20;
`;

const WorldMap = ({
      selectedLocation,
      setSelectedLocation,
      clusterIsActiveMap = {},
  }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);

    const [countries, setCountries] = useState(null);
    const [dimensions, setDimensions] = useState({
        width: 800,
        height: 500,
    });

    const [tooltip, setTooltip] = useState(null);

    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const countryLookup = useMemo(() => {
        const lookup = {};

        areas.forEach((area) => {
            if (!area.country) return;

            lookup[area.country.toLowerCase()] = {
                ...area,
                isActive: Boolean(clusterIsActiveMap[area.value]),
                isSelected: selectedLocation === area.value,
            };
        });

        return lookup;
    }, [clusterIsActiveMap, selectedLocation]);

    useEffect(() => {
        let cancelled = false;

        const loadMap = async () => {
            try {
                const response = await fetch(MAP_URL);

                if (!response.ok) {
                    throw new Error(`Map request failed: ${response.status}`);
                }

                const topology = await response.json();

                if (cancelled) return;

                const geojson = feature(
                    topology,
                    topology.objects.countries
                );

                // Filter out Antarctica
                geojson.features = geojson.features.filter((country) => {
                    const id = country.id;
                    const name = country.properties?.name;
                    return id !== '010' && name?.toLowerCase() !== 'antarctica';
                });

                setCountries(geojson);
            } catch (error) {
                console.error('Failed to load world map:', error);
            }
        };

        loadMap();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            const rect = entries[0].contentRect;

            setDimensions({
                width: Math.max(rect.width, 300),
                height: Math.max(rect.height, 300),
            });
        });

        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    const projection = useMemo(() => {
        if (!countries) return null;

        const { width, height } = dimensions;
        const baseScale = 280;

        return d3
            .geoNaturalEarth1()
            .scale(baseScale)
            .translate([width / 3.8 + position.x, height / 2 + 150 + position.y]);
    }, [countries, dimensions, position]);

    const pathGenerator = useMemo(() => {
        if (!projection) return null;

        return d3.geoPath(projection);
    }, [projection]);

    /*
     * Mouse Handlers for Pan only
     */
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const getCountryConfig = (country) => {
        const name = country.properties?.name;

        if (!name) return null;

        return countryLookup[name.toLowerCase()] || null;
    };

    const handleCountryClick = (country) => {
        const config = getCountryConfig(country);

        if (!config) return;

        setSelectedLocation(config.value);
    };

    const handleCountryMouseMove = (event, country) => {
        const config = getCountryConfig(country);

        if (!config) return;

        const rect = containerRef.current.getBoundingClientRect();

        setTooltip({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            name: config.country,
            status: config.isActive ? 'Operational' : 'Not operational',
        });
    };

    const getCountryStyle = (country) => {
        const config = getCountryConfig(country);

        if (!config) {
            return {
                fill: '#17212b',
                stroke: '#263642',
                opacity: 0.75,
            };
        }

        if (config.isActive) {
            return {
                fill: '#2e7d32',
                stroke: '#4caf50',
                opacity: 0.95,
            };
        }

        return {
            fill: '#d84315',
            stroke: '#ff7043',
            opacity: 0.9,
        };
    };

    if (!countries || !projection || !pathGenerator) {
        return (
            <MapWrapper ref={containerRef} $isDragging={false}>
                <Loading>Loading world map...</Loading>
            </MapWrapper>
        );
    }

    return (
        <MapWrapper
            ref={containerRef}
            $isDragging={isDragging}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            <MapSvg
                ref={svgRef}
                viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                preserveAspectRatio="xMidYMid meet"
            >
                {/* Ocean glow */}
                <rect
                    width={dimensions.width}
                    height={dimensions.height}
                    fill="transparent"
                />

                {/* Countries */}
                <g>
                    {countries.features.map((country) => {
                        const style = getCountryStyle(country);
                        const config = getCountryConfig(country);

                        return (
                            <path
                                key={country.id || country.properties?.name}
                                className="country"
                                d={pathGenerator(country)}
                                fill={style.fill}
                                stroke={style.stroke}
                                strokeWidth={
                                    config?.isSelected
                                        ? 1.5
                                        : config
                                            ? 0.8
                                            : 0.4
                                }
                                opacity={style.opacity}
                                style={{
                                    cursor: config ? 'pointer' : 'inherit',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCountryClick(country);
                                }}
                                onMouseMove={(e) =>
                                    handleCountryMouseMove(e, country)
                                }
                                onMouseLeave={() => setTooltip(null)}
                            />
                        );
                    })}
                </g>
            </MapSvg>

            {tooltip && (
                <Tooltip
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                    }}
                >
                    <strong>{tooltip.name}</strong>
                    <br />
                    {tooltip.status}
                </Tooltip>
            )}
        </MapWrapper>
    );
};

export default WorldMap;